import { useEffect } from "react";
import { observer } from "mobx-react";
import { Box } from "@mui/material";

import AppealsIcon from "../../../icons/AppealsIcon";
import DocumentIcon from "../../../icons/DocumentIcon";
import DollarIcon from "../../../icons/DollarIcon";
import TrendingUpIcon from "../../../icons/TrendingUpIcon";
import WarningAmberIcon from "../../../icons/WarningAmberIcon";

import { useChatStore } from "../../../../hooks/useChatStore";
import { ExtendedHomeStatisticsCard } from "./ExtendedHomeStatisticsCard";

// Recovered amount can be negative (a payer taking money back), so format the
// magnitude and put the sign in front of the "$".
const formatDollars = (amount: number): string => {
  const formatted = formatAbsoluteDollars(Math.abs(amount));
  return amount < 0 && formatted !== "$0.00" ? `-${formatted}` : formatted;
};

const formatAbsoluteDollars = (amount: number): string => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(2)}M`;
  }
  if (amount >= 1000) {
    const thousands = (amount / 1000).toFixed(1);
    // toFixed's rounding can push e.g. 999,999.99 to "1000.0" here, which
    // would render as "$1000.0K"; re-render that case as millions instead.
    if (Number(thousands) >= 1000) {
      return `$${(amount / 1000000).toFixed(2)}M`;
    }
    return `$${thousands}K`;
  }
  return `$${amount.toFixed(2)}`;
};

export const ExtendedHomeStatisticsSection = observer(() => {
  const chatStore = useChatStore();
  const {
    dashboardStatistics,
    isDashboardStatisticsLoading,
    hasDashboardStatisticsError,
  } = chatStore;
  // Backends older than app-backend#734 don't send recoveredAmount; show the
  // dash rather than a "$0.00" that reads like a real figure.
  const isRecoveredAmountMissing =
    dashboardStatistics != null &&
    dashboardStatistics.recoveredAmount === undefined;

  useEffect(() => {
    chatStore.loadDashboardStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        gap: 1.5,
        gridTemplateColumns: "1fr",
        "@container home-content (min-width: 480px)": {
          gridTemplateColumns: "repeat(2, 1fr)",
          // Odd card count: let the last card fill the row instead of
          // sitting alone in the left column.
          "& > :last-child": { gridColumn: "1 / -1" },
        },
        // Five columns only once each card is wide enough for its noWrap
        // label ("Appeals year to date") not to be truncated.
        "@container home-content (min-width: 1200px)": {
          gridTemplateColumns: "repeat(5, 1fr)",
          "& > :last-child": { gridColumn: "auto" },
        },
      }}
    >
      <ExtendedHomeStatisticsCard
        icon={<DocumentIcon />}
        value={dashboardStatistics?.openCases ?? 0}
        label="Open cases"
        isLoading={isDashboardStatisticsLoading}
        hasError={hasDashboardStatisticsError}
      />

      <ExtendedHomeStatisticsCard
        icon={<WarningAmberIcon />}
        value={dashboardStatistics?.claimsInProgress ?? 0}
        label="Claims in progress"
        isLoading={isDashboardStatisticsLoading}
        hasError={hasDashboardStatisticsError}
      />

      <ExtendedHomeStatisticsCard
        icon={<DollarIcon />}
        value={formatDollars(dashboardStatistics?.dollarsAtRisk ?? 0)}
        label="Dollars at risk"
        isLoading={isDashboardStatisticsLoading}
        hasError={hasDashboardStatisticsError}
      />

      <ExtendedHomeStatisticsCard
        icon={<AppealsIcon />}
        value={dashboardStatistics?.appealsYearToDate ?? 0}
        label="Appeals year to date"
        isLoading={isDashboardStatisticsLoading}
        hasError={hasDashboardStatisticsError}
      />

      <ExtendedHomeStatisticsCard
        icon={<TrendingUpIcon />}
        value={formatDollars(dashboardStatistics?.recoveredAmount ?? 0)}
        label="Recovered amount"
        isLoading={isDashboardStatisticsLoading}
        hasError={hasDashboardStatisticsError || isRecoveredAmountMissing}
      />
    </Box>
  );
});

export default ExtendedHomeStatisticsSection;
